import {
  Component,
  HostListener,
  Inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  PLATFORM_ID
} from '@angular/core';
import { assets } from '../../../core/constants/assets.const';
import { RouterModule } from '@angular/router';
import { MemberStep1Component } from '../../../modals/become-member/member-step1/member-step1.component';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../../services/auth.service';
import { SpinnerService } from '../../../services/spinner.service';
import { Subject, takeUntil } from 'rxjs';
import { ToasterService } from '../../../services/toaster.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MemberStep2Component } from '../../../modals/become-member/member-step2/member-step2.component';
import { SuccessComponent } from '../../../modals/success/success.component';
import {
  BankDetails,
  vendorCompleteSignUpPayload,
  VendorSignUp
} from '../../../models/vendor.model';
import { UploadDocumentComponent } from '../../../modals/become-member/upload-document/upload-document.component';
import {
  Document,
  documentFormResponse
} from '../../../models/documents.model';
import { CMS } from '../../../core/constants/cms.const';
import { MatIconModule } from '@angular/material/icon';
import { responseStatus } from '../../../core/constants/responseStatus.const';
import { CommonResponse } from '../../../models/common.model';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'xplora-website-footer',
  standalone: true,
  imports: [RouterModule, TranslateModule, MatIconModule, CommonModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent implements OnDestroy, OnInit {
  protected assetsConst = assets;
  private destroy: Subject<boolean> = new Subject<boolean>();
  protected cmsContent = CMS;
  formData: VendorSignUp | null = null;
  phoneNumber = '+971545888785';
  message = 'Hello, Xplora!';

  protected vendorId: string = '';
  protected currentYear: number | null = null;

  constructor(
    public dialog: MatDialog,
    private spinnerService: SpinnerService,
    private _authService: AuthService,
    private _toastrService: ToasterService,
    private _translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<object>
  ) {}

  showButton = false;
  @HostListener('window:scroll', [])
  onWindowScroll(): any {
    if (isPlatformBrowser(this.platformId)) {
      const scrollPosition =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
      this.showButton = scrollPosition > 200; // Show button after scrolling down 200px
    }
  }

  ngOnInit(): void {
    this.currentYear = new Date().getFullYear();
  }
  // Scroll to top
  scrollToTop(): any {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0,
        behavior: 'smooth' });
    }
  }

  openMemberStep1Modal(): void {
    const dialogRef = this.dialog.open(MemberStep1Component, {
      panelClass: ['modal', 'auth_modal', 'setup_modal']
    });
    dialogRef.afterClosed().subscribe((res: VendorSignUp) => {
      if (res) {
        this.formData = res;
        this.addBasicDetails();
      }
    });
  }

  addBasicDetails(): void {
    this.spinnerService.addToLoader('VendorSignUp');
    this._authService
      .vendorSignUp(this.formData ? this.formData : null)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: VendorSignUp) => {
          this.spinnerService.removeFromLoader('VendorSignUp');
          if (res._id) {
            this.vendorId = res._id;
            this.getReqDocs();
          }
        },
        error: () => {
          this.spinnerService.removeFromLoader('VendorSignUp');
        }
      });
  }

  getReqDocs(): void {
    this.spinnerService.addToLoader('getRequiredDocs');
    this._authService.getDocuments().subscribe({
      next: (res: Document[]) => {
        this.spinnerService.removeFromLoader('getRequiredDocs');
        if (res && res.length) {
          this.UploadDocument(res);
        } else {
          this.openSuccessModal();
        }
      },
      error: () => {
        this.spinnerService.removeFromLoader('getRequiredDocs');
      }
    });
  }

  UploadDocument(docsArray: Document[]): void {
    const dialogRef = this.dialog.open(UploadDocumentComponent, {
      panelClass: ['modal', 'auth_modal', 'setup_modal', 'doc_modal'],
      data: docsArray
    });
    dialogRef.afterClosed().subscribe((res: documentFormResponse) => {
      if (res) {
        this.handleSecondApiHit(res);
      }
    });
  }

  handleSecondApiHit(res: documentFormResponse): void {
    const payload: vendorCompleteSignUpPayload = {
      vendorId: this.vendorId,
      status: 0,
      documents: res.documents
    };
    this.spinnerService.addToLoader('VendorSignUp');
    this._authService
      .vendorSignUpStep2(payload)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: CommonResponse) => {
          this.spinnerService.removeFromLoader('VendorSignUp');
          if (res.statusCode === responseStatus.SUCCESS) {
            this._toastrService.successToastr(
              this._translateService.instant('VendorSignUpDone')
            );
            this.openSuccessModal();
          }
        },
        error: () => {
          this.spinnerService.removeFromLoader('VendorSignUp');
        }
      });
  }

  openSuccessModal(): void {
    const dialogRef = this.dialog.open(SuccessComponent, {
      panelClass: ['modal'],
      data: {
        becomeMember: true
      }
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  openMemberStep2Modal(): void {
    const dialogRef = this.dialog.open(MemberStep2Component, {
      panelClass: ['modal', 'auth_modal', 'setup_modal']
    });
    dialogRef.afterClosed().subscribe((res: BankDetails) => {
      if (res && this.formData) {
        this.formData = {
          ...this.formData,
          bankDetails: res
        };
        this.getReqDocs();
      }
    });
  }

  get whatsappLink(): string {
    return `https://wa.me/${this.phoneNumber}?text=${encodeURIComponent(this.message)}`;
  }

  ngOnDestroy(): void {
    this.destroy.complete();
    this.destroy.unsubscribe();
  }
}
